const cyberFitnessExercises = {
    exercises: [
        {
            about: 'Vmware Horizon Administrator is the Web interface through which you configure Horizon Connection Server and manage your remote desktops and applications. VMware Horizon is a virtualization solution that could contain virtual machines of varying importance.',
            affected_hosts: [
                {
                    forward_reverse_dns: [
                        'daas.bajajallianz.com',
                    ],
                    target: '103.153.88.91',
                },
            ],
            effort: 1,
            hackability_share: 1.8173557473875512,
            issues: [
                {
                    accepted_on: null,
                    cves: null,
                    fixed_on: null,
                    hosts: [
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'daas.bajajallianz.com',
                            ],
                            port: 443,
                            protocol: 'tcp',
                            service: 'https',
                            shortname: 'vmware_horizon_exposed',
                            target: '103.153.88.91',
                        },
                    ],
                    shortname: 'vmware_horizon_exposed',
                },
            ],
            mitigation: 'The vulnerability can be mitigated through allowing connection only from a jumphost and by moving Vmware Horizon servers to a separate VLAN.\n\n## Remediation option 1 - Allowing connection only from a jumphost\n\n- [ ]  **Allow connection only from a jumphost**\n\nThis approach involves the creation of a jump host from which HP servers can be reached. By doing this, only system administrators will have jump host user credentials, so the servers will only be reachable by them.\n\nMore information on how to set up a jump host and allow certain connections:\n\n1. [Setup an SSH server](https://linuxize.com/post/how-to-enable-ssh-on-ubuntu-18-04/)\n2. [Authorize users](https://linuxhandbook.com/add-ssh-public-key-to-server/)\n3. [Access the Vmware Horizon servers](https://www.tecmint.com/access-linux-server-using-a-jump-host/)\n\n## Mitigation option 2 - Move Vmware Horizon servers to a separate VLAN\n\n- [ ]  **Move Vmware Horizon servers to a separate VLAN**\n\nCreate a new VLAN that can only be reached from the admin VLAN and move every Vmware Horizon server inside so no attacker or common user can reach the web interface.\n\nMore information on how to create a VLAN: [Cisco](https://www.cisco.com/c/en/us/td/docs/switches/lan/catalyst2960/software/release/12-2_40_se/configuration/guide/scg/swvlan.pdf)',
            number_of_affected_hosts: 1,
            objective: 'Today the aim is to protect VMware Horizon management interface from being exposed. This can be achieved in two different approaches:\n\n1. Allowing connection only from a jumphost\n2. Move Vmware Horizon servers to separate VLAN',
            risk: 'Successful exploitation of this issue may allow disclosure of internal domain names, the Connection Server’s internal name, or the gateway’s internal IP address.',
            title: 'Do not expose VMware Horizon management interface',
            tutorial_name: 'exposure_vmware_horizon',
        },
        {
            about: 'FTP, or file transfer protocol, is a way to transfer files between computers.',
            affected_hosts: [
                {
                    forward_reverse_dns: [
                        'eulerhermes.com.pl',
                        'syntesys.com.pl',
                        'domains001.home.pl',
                    ],
                    target: '89.161.255.4',
                },
            ],
            effort: 1,
            hackability_share: 0,
            issues: [
                {
                    accepted_on: null,
                    cves: null,
                    fixed_on: null,
                    hosts: [
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'eulerhermes.com.pl',
                                'syntesys.com.pl',
                                'domains001.home.pl',
                            ],
                            port: 21,
                            protocol: 'tcp',
                            service: 'ftp',
                            shortname: 'anon_ftp_empty',
                            target: '89.161.255.4',
                        },
                    ],
                    shortname: 'anon_ftp_empty',
                },
            ],
            mitigation: 'In this exercise, we remove anonymous access for the three most common FTP services: ProFTPD, vsFTPd, and cPanel. While cPanel is a web hosting control panel, and not an FTP server, it can also be used to assure you do not expose anonymous writable FTP login. \n\nThe steps need to be executed for each of the servers mentioned in **Affected hosts** below. \n\n> :bulb: **Note**  \n> Before removing anonymous access, please check if the server is configured this way on purpose. In general, it is advised to never have anonymous write access for users, but if you really need to, then risk-accept this exercise  \n\n> :bulb: **Note**  \n> Please note that throughout this exercise we have used vim as editor. Feel free to use any editor you prefer\n\n### Remediation option 1 - Disable writable anonymous access\n\n### 1. ProFTPD\n\n- [ ]  **1.1 - Open the proftpd.conf file**\n\nOpen the `proftpd.conf` file. Its default location is `/etc/proftpd.conf` To open it, run the following command in the Terminal:\n`$ sudo vim /etc/proftpd.conf`\n\n- [ ]  **1.2 - Edit proftpd.conf**\n\nAdd `<Limit WRITE>` in the the `<Anonymous>` section. Please see an example of section that has to be modified below.\n\n```\n<Anonymous /var/ftp/anon/dave>\n    User dave\n    Group ftpanon\n\t\t<Limit WRITE>\n    ...\n</Anonymous>\n```\n\n- [ ]  **1.3 - Restart proftpd**\n\nRestart the service by running the following command in the Terminal: \n`$ sudo service proftpd restart`\n\n### 2. vsFTPd\n\n- [ ]  **2.1 - Open the vsftpd.conf file**\n\nOpen the `vsftpd.conf` file. Its default location is `/etc/vsftpd/vsftpd.conf` To open it, run the following command in the Terminal:\n`$ sudo vim /etc/vsftpd/vsftpd.conf`\n\n- [ ]  **2.2 - Edit vsftpd.conf**\n\nCheck if `anon_upload_enable=YES` exists. If yes, change `YES` to `NO` so it looks like the example below:\n\n`anon_upload_enable=NO`\n\n- [ ]  **2.3 - Restart vsftpd**\n\nRestart the service by running the following command in the Terminal: \n`$ sudo service vsftpd restart`\n\n### 3. cPanel\n\n- [ ]  **3.1 - Log into cPanel**\n\nGenerally, you can login to cPanel by navigating to [example.com/cpanel](http://example.com/cpanel). Be sure to replace [example.com](http://example.com/) with your actual domain\n\n- [ ]  **3.2 - Navigate to Anonymous FTP**\n\nIn the **Files** section, click the Anonymous FTP icon (shown in the image below)\n\n![https://rempack.s3.eu-central-1.amazonaws.com/b908080a-6783-499d-bb8d-4e13362ddf8c.png](https://rempack.s3.eu-central-1.amazonaws.com/b908080a-6783-499d-bb8d-4e13362ddf8c.png)\n\n- [ ]  **3.3 - Uncheck Allow anonymous uploads**\n\nUnder Anonymous FTP Controls, uncheck the **Allow anonymous uploads** box\n\n- [ ]  **3.4 - Save settings and exit**\n\nClick **Save Settings** and exit cPanel\n\n### Remediation option 2 - Disable anonymous access\n\n### 1. ProFTPD\n\n- [ ]  **1.1 - Open the proftpd.conf file**\n\nOpen the `proftpd.conf` file. Its default location is `/etc/proftpd.conf` To open it, run the following command in the Terminal:\n`$ sudo vim /etc/proftpd.conf`\n\n- [ ]  **1.2 - Edit proftpd.conf**\n\nRemove the `<Anonymous>` section. Example of section that has to be removed below:\n\n```\n<Anonymous /var/ftp/anon/dave>\n    User dave\n    Group ftpanon\n    ...\n</Anonymous>\n```\n\n- [ ]  **1.3 - Restart proftpd**\n\nRestart the service by running the following command in the Terminal: \n`$ sudo service proftpd restart`\n\n### 2. vsFTPd\n\n- [ ]  **2.1 - Open the vsftpd.conf file**\n\nOpen the `vsftpd.conf` file. Its default location is `/etc/vsftpd/vsftpd.conf` To open it, run the following command in the Terminal:\n`$ sudo vim /etc/vsftpd/vsftpd.conf`\n\n- [ ]  **2.2 - Edit vsftpd.conf**\n\nChange `anonymous_enable=YES`to `anonymous_enable=NO` and make sure that `anonymous_enable=YES` is removed or uncommented with "#".\n\n- [ ]  **2.3 - Restart vsftpd**\n\nRestart the service by running the following command in the Terminal: \n`$ sudo service vsftpd restart`\n\n### 3. cPanel\n\n- [ ]  **3.1 - Log into cPanel**\n\nGenerally, you can login to cPanel by navigating to [example.com/cpanel](http://example.com/cpanel). Be sure to replace [example.com](http://example.com/) with your actual domain\n\n- [ ]  **3.2 - Navigate to Anonymous FTP**\n\nIn the **Files** section, click the Anonymous FTP icon (shown in the image below)\n\n![https://rempack.s3.eu-central-1.amazonaws.com/b908080a-6783-499d-bb8d-4e13362ddf8c.png](https://rempack.s3.eu-central-1.amazonaws.com/b908080a-6783-499d-bb8d-4e13362ddf8c.png)\n\n- [ ]  **3.3 - Uncheck Allow anonymous access**\n\nUnder Anonymous FTP Controls, uncheck the **Allow anonymous access** box\n\n- [ ]  **3.4 - Save settings and exit**\n\nClick **Save Settings** and exit cPanel',
            number_of_affected_hosts: 1,
            objective: 'The aim of this exercise is to remove the ability for anonymous FTP users to upload files to an FTP server. This can be solved either by completely disabling anonymous FTP access, or by disabling anonymous writable access to an FTP server.',
            risk: 'Anonymous FTP is an implementation of an FTP server that allows anyone to log on to the server using a general username, without password check. The FTP server allows an anonymous user to (over)write files. This can often be used in combination with other services, such as HTTP, to compromise the server.',
            title: 'Prevent FTP write access for anonymous users',
            tutorial_name: 'hardening_ftp_disable_anonymous_write',
        },
        {
            about: 'Server Message Block (SMB) is a network protocol for sharing files and printers. It is the basis of Windows file sharing, and TCP 139 and 445 ports are used by default. ',
            affected_hosts: [
                {
                    forward_reverse_dns: [],
                    target: '124.156.147.48',
                },
                {
                    forward_reverse_dns: [
                        'ehaci.com',
                        'www.ehaci.com',
                    ],
                    target: '156.251.243.74',
                },
                {
                    forward_reverse_dns: [
                        'msaccess.advania.is',
                        'sip.advania.is',
                        'sipexternal.advania.is',
                    ],
                    target: '212.30.227.129',
                },
                {
                    forward_reverse_dns: [
                        'mswebcon.advania.is',
                    ],
                    target: '212.30.227.130',
                },
                {
                    forward_reverse_dns: [
                        'msav.advania.is',
                    ],
                    target: '212.30.227.131',
                },
                {
                    forward_reverse_dns: [
                        'm.optiteque.com',
                        'optiteque.com',
                        'www.optiteque.com',
                    ],
                    target: '23.231.104.5',
                },
            ],
            effort: 1,
            hackability_share: 5.997273966378919,
            issues: [
                {
                    accepted_on: null,
                    cves: null,
                    fixed_on: null,
                    hosts: [
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 445,
                            protocol: 'tcp',
                            service: 'microsoft-ds',
                            shortname: 'smb_v1',
                            target: '124.156.147.48',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ehaci.com',
                                'www.ehaci.com',
                            ],
                            port: 445,
                            protocol: 'tcp',
                            service: 'microsoft-ds',
                            shortname: 'smb_v1',
                            target: '156.251.243.74',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'msaccess.advania.is',
                                'sip.advania.is',
                                'sipexternal.advania.is',
                            ],
                            port: 445,
                            protocol: 'tcp',
                            service: 'microsoft-ds',
                            shortname: 'smb_v1',
                            target: '212.30.227.129',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mswebcon.advania.is',
                            ],
                            port: 445,
                            protocol: 'tcp',
                            service: 'microsoft-ds',
                            shortname: 'smb_v1',
                            target: '212.30.227.130',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'msav.advania.is',
                            ],
                            port: 445,
                            protocol: 'tcp',
                            service: 'microsoft-ds',
                            shortname: 'smb_v1',
                            target: '212.30.227.131',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'm.optiteque.com',
                                'optiteque.com',
                                'www.optiteque.com',
                            ],
                            port: 445,
                            protocol: 'tcp',
                            service: 'microsoft-ds',
                            shortname: 'smb_v1',
                            target: '23.231.104.5',
                        },
                    ],
                    shortname: 'smb_v1',
                },
            ],
            mitigation: 'The steps need to be executed for each of the servers mentioned in **Affected hosts** below. They differ between Windows and Linux servers. \n\n### Windows\n\nThree options are available ,.. You only need to apply one of them to remediate the issue.\n\n### 1. PowerShell\n\n- [ ]  **1.1 - Open PowerShell**\n- Click **Start**\n- Type **run** and hit enter\n- Write **PowerShell** in the Search box and hit enter\n\n- [ ]  **1.2 - Enable SMBv2/3**\n\nExecute in PowerShell\n\n```powershell\nSet-ItemProperty -Path "HKLM:\\SYSTEM\\CurrentControlSet\\Services\\LanmanServer\\Parameters" SMB2 -Type DWORD -Value 1 -Force\n```\n\n- [ ]  **1.3 - Disable SMBv1**\n\nExecute in PowerShell\n\n```powershell\nSet-ItemProperty -Path "HKLM:\\SYSTEM\\CurrentControlSet\\Services\\LanmanServer\\Parameters" SMB1 -Type DWORD -Value 0 -Force\n```\n\n> :bulb: **Note**  \n> Please restart your system to apply the changes\n\n### 2. Registry Editor method\n\n- [ ]  **2.1 - Open the Registry Editor**\n- Click **Start**\n- Type **regedit** in the Search box, click enter\n\n- [ ]  **2.2 - Enable SMBv2/3**\n\nNavigate to the following registry key: `HKEY_LOCAL_MACHINE\\SYSTEM\\CurrentControlSet\\Services\\LanmanServer\\Parameters`\n\nIf the `SMB2` registry entry exists, edit it and set its value to **1**\n\nIf the `SMB2` registry entry does not exist, create it as a **REG_DWORD** type and set its value to **1**\n\n- [ ]  **2.3 - Disable SMBv1**\n\nNavigate to the following registry key: `HKEY_LOCAL_MACHINE\\SYSTEM\\CurrentControlSet\\Services\\LanmanServer\\Parameters`\n\nIf the `SMB1` registry entry exists, edit it and set its value to **0**\n\nIf the `SMB1` registry entry does not exist, create it as a **REG_DWORD** type and set its value to **0**\n\n> :bulb: **Note**  \n> Please restart your system to apply the changes\n\n### 3. Windows features method\n\nWindows 8.1 and Windows 10 users can also use the Windows features to add or remove SMBv1\n\n![https://rempack.s3.eu-central-1.amazonaws.com/96a1f12f-9f1e-4af0-a1cf-b88d98d6d185.png](https://rempack.s3.eu-central-1.amazonaws.com/96a1f12f-9f1e-4af0-a1cf-b88d98d6d185.png)\n\n- [ ]  **3.1 - Open the Windows features box**\n\nOpen the Windows control panel via the settings. Here, select **Programs and Features**\n\nAt **Control Panel Home,** select **Turn Windows features on or off** to open the **Windows features box**\n\n- [ ]  **3.2 - Untick SMB 1.0/CISF File Sharing Support**\n\nIn the **Windows features** box, scroll down the list and uncheck the box for **SMB 1.0/CISF File Sharing Support**. Afterwards, hit **OK**\n\n- [ ]  **3.3 - Restart Windows**\n\nAfter Windows applies the change, on the confirmation page, select **Restart now**\n\n### Linux\n\n> :bulb: **Note**  \n> Please note that throughout this exercise we have used **vim** as editor. Feel free to use any editor you prefer\n\n- [ ]  **1.1 - Identify which Samba version is running on the server**\n\nExecute the following command in the Terminal:\n\n```bash\n\tsudo smbd -V\n```\n\n- [ ]  **1.2 - If Samba 3.5 or below: upgrade Samba**\n\nBased on your Linux distribution, choose one of the following methods to upgrade Samba and type it in your Terminal\n\n**Ubuntu**\n\n```bash\nsudo apt update\nsudo apt install samba\n```\n\n**RedHat / CentOS**\n\n```bash\nsudo yum check-update\nsudo yum update samba\n```\n\n- [ ]  **1.3 - Disable SMBv1**\n\nOpen the `smb.conf` file. Its default location is `/etc/smb.conf` To open it, run the following command in the Terminal:\n`$ sudo vim /etc/smb.conf`\n\nIntroduce the following content just below the **[global]** section:\n\n`min protocol = SMB2`\n\n![https://rempack.s3.eu-central-1.amazonaws.com/90633191-5457-4096-b1a8-97b9ca9fefe8.png](https://rempack.s3.eu-central-1.amazonaws.com/90633191-5457-4096-b1a8-97b9ca9fefe8.png)\n\n- [ ]  **1.4 - Restart the service**\n\nRestart the service by running one of the following commands in the Terminal:\n\n```bash\nsudo systemctl restart smb.service\n```\n\nor\n\n```bash\nsudo systemctl restart smbd.service\n```\n\nGreat job, you are all done!',
            number_of_affected_hosts: 6,
            objective: 'Today we are disabling the older version of Server Message Block, SMBv1. The exact steps will differ between Windows and Linux servers. ',
            risk: 'The SMBv1 protocol was designed for Windows 95. SMBv1 has been succeeded by more secure versions, who added additional security features:\n\n- **Better message signing (SMB 2.02+):** HMAC SHA-256 replaced MD5 as the hashing algorithm in SMB 2.02 and SMB 2.1. AES-CMAC replaced this in SMB 3.0+\n- **Encryption (SMB 3.0+):** prevents inspection of data on the wire, as well as against man-in-the-middle attacks (MITM). In SMB 3.1.1, encryption performance is even better than signing\n- **Secure dialect negotiation (SMB 3.0, 3.02):** protects against security downgrade attacks\n- **Pre-authentication integrity (SMB 3.1.1+):** protects against security downgrade attacks',
            title: 'Disable SMBv1',
            tutorial_name: 'hardening_smb_disable_v1',
        },
        {
            about: 'Secure Shell (SSH) is a cryptographic network protocol for operating network services securely over an unsecured network. Using SSH with public keys is generally considered more secure than passwords.',
            affected_hosts: [
                {
                    forward_reverse_dns: [],
                    target: '1.253.240.3',
                },
                {
                    forward_reverse_dns: [
                        'gtins.com.au',
                        'www.gtins.com.au',
                        '174.111.0.101.static.digitalpacific.com.au',
                    ],
                    target: '101.0.111.174',
                },
                {
                    forward_reverse_dns: [
                        'primacywww.pum.co.nz',
                        'primacywww.pum.com.au',
                        'tommy-alias.bnr.la',
                    ],
                    target: '103.1.186.60',
                },
                {
                    forward_reverse_dns: [
                        'cpanel.agamedicalnetwork.com',
                        'mail.agamedicalnetwork.com',
                        'webdisk.agamedicalnetwork.com',
                        'webmail.agamedicalnetwork.com',
                        'whm.agamedicalnetwork.com',
                        'md-in-36.webhostbox.net',
                    ],
                    target: '103.21.58.29',
                },
                {
                    forward_reverse_dns: [
                        'link.eh-dach.de',
                        '78.54.154.104.bc.googleusercontent.com',
                    ],
                    target: '104.154.54.78',
                },
                {
                    forward_reverse_dns: [
                        'ip-107-180-41-243.ip.secureserver.net',
                    ],
                    target: '107.180.41.243',
                },
                {
                    forward_reverse_dns: [
                        'admin.eolisguide.com',
                        'autoconfig.admin.eolisguide.com',
                        'autoconfig.eolisguide.com',
                        'autodiscover.admin.eolisguide.com',
                        'autodiscover.eolisguide.com',
                        'cpanel.eolisguide.com',
                        'eolisguide.com',
                        'ftp.eolisguide.com',
                        'mail.eolisguide.com',
                        'webdisk.admin.eolisguide.com',
                        'webdisk.eolisguide.com',
                        'webmail.eolisguide.com',
                        'whm.eolisguide.com',
                        'www.admin.eolisguide.com',
                        'www.eolisguide.com',
                        'ip-107-180-50-234.ip.secureserver.net',
                    ],
                    target: '107.180.50.234',
                },
                {
                    forward_reverse_dns: [
                        'apidev.allianz.com.my',
                    ],
                    target: '110.74.163.72',
                },
                {
                    forward_reverse_dns: [
                        'oneglobalshop.allianzgi.com',
                    ],
                    target: '116.202.201.35',
                },
                {
                    forward_reverse_dns: [
                        'allianz-x.com',
                        'allianz-x.de',
                        'www.allianz-x.com',
                        'www.allianz-x.de',
                        'steinrein1.timmeserver.de',
                    ],
                    target: '116.202.231.184',
                },
                {
                    forward_reverse_dns: [],
                    target: '12.234.93.62',
                },
                {
                    forward_reverse_dns: [
                        'h2web217.infomaniak.ch',
                    ],
                    target: '128.65.195.127',
                },
                {
                    forward_reverse_dns: [
                        'veranstaltungshaftpflicht.allianz.de',
                        'cw22.virtualhosts.de',
                    ],
                    target: '130.255.79.74',
                },
                {
                    forward_reverse_dns: [
                        'credit-bleu.fr',
                        'mon-rachat-de-credit.fr',
                        'jazz.ispgateway.de',
                    ],
                    target: '134.119.45.85',
                },
                {
                    forward_reverse_dns: [],
                    target: '134.213.212.77',
                },
                {
                    forward_reverse_dns: [],
                    target: '138.197.103.178',
                },
                {
                    forward_reverse_dns: [
                        'ip4.ip-142-4-223.net',
                    ],
                    target: '142.4.223.4',
                },
                {
                    forward_reverse_dns: [
                        'www.medienplan.biz',
                        'www.medienplan.com',
                        'www.medienplan.info',
                        'ajtakreview.com',
                    ],
                    target: '144.172.64.31',
                },
                {
                    forward_reverse_dns: [
                        'adamasiapac.com',
                        'www.adamasiapac.com',
                    ],
                    target: '154.201.160.53',
                },
                {
                    forward_reverse_dns: [
                        'controlexpert.com',
                        'international.ce-systeme.de',
                        'test1.ce-systeme.de',
                        'test2.ce-systeme.de',
                        'web-cdn.controlexpert.com',
                        'www.controlexpert.com',
                        'www.test1.ce-systeme.de',
                        'www.test2.ce-systeme.de',
                        'www.www2.controlexpert.com',
                        'www2.controlexpert.com',
                    ],
                    target: '156.67.236.104',
                },
                {
                    forward_reverse_dns: [
                        'allianzx.com',
                        'www.allianzx.com',
                        'werkraum1.timmeserver.de',
                    ],
                    target: '157.90.212.141',
                },
                {
                    forward_reverse_dns: [
                        'newsletter.borgosanfelice.it',
                    ],
                    target: '159.89.101.21',
                },
                {
                    forward_reverse_dns: [
                        'mail.hospeda.mx',
                    ],
                    target: '165.227.43.209',
                },
                {
                    forward_reverse_dns: [
                        'sftp.roggeglobal.com',
                    ],
                    target: '168.114.112.34',
                },
                {
                    forward_reverse_dns: [
                        'd1.02.33a9.ip4.static.sl-reverse.com',
                    ],
                    target: '169.51.2.209',
                },
                {
                    forward_reverse_dns: [],
                    target: '171.22.127.229',
                },
                {
                    forward_reverse_dns: [
                        'ftp.mondial-projects.com',
                        'www.mondial-projects.com',
                        'ip-173-201-254-177.ip.secureserver.net',
                    ],
                    target: '173.201.254.177',
                },
                {
                    forward_reverse_dns: [
                        'investmentdataservices.info',
                        'www.investmentdataservices.info',
                    ],
                    target: '176.52.243.57',
                },
                {
                    forward_reverse_dns: [
                        'business-176-094-147-058.static.arcor-ip.net',
                    ],
                    target: '176.94.147.58',
                },
                {
                    forward_reverse_dns: [
                        'jaideunprochedependant.fr',
                        'www.jaideunprochedependant.fr',
                    ],
                    target: '178.32.17.45',
                },
                {
                    forward_reverse_dns: [
                        'lvps178-77-101-114.dedicated.hosteurope.de',
                    ],
                    target: '178.77.101.114',
                },
                {
                    forward_reverse_dns: [
                        'allianz-arbeitssicherheit.de',
                        'allianz-learning.com',
                        'allianzseminare.de',
                        'www.allianz-learning.com',
                        'www.allianzseminare.de',
                    ],
                    target: '178.77.89.84',
                },
                {
                    forward_reverse_dns: [
                        'ftp.allianzatoz.com',
                        'ftp.allianzblue.com',
                        'ftp.allianzincomefocus.com',
                    ],
                    target: '184.106.55.230',
                },
                {
                    forward_reverse_dns: [
                        'berner.ch',
                        'mail.policencheck.ch',
                        'mail.policencheck.li',
                        'mail.splitsurance.ch',
                        'mail.splitsurance.com',
                        'mail.splitsurance.li',
                        'policencheck.ch',
                        'policencheck.li',
                        'splitsurance.ch',
                        'splitsurance.com',
                        'splitsurance.li',
                        'www-smtp.splitsurance.ch',
                        'www.splitsurance.ch',
                        'www.splitsurance.com',
                        'www.splitsurance.li',
                        'wwwq.splitsurance.ch',
                        'default.hosttech.eu',
                    ],
                    target: '185.101.158.113',
                },
                {
                    forward_reverse_dns: [
                        'allianz-realestate.com',
                        'www.allianz-real-estate.com',
                        'www.allianz-real-estate.de',
                        'www.allianz-realestate.com',
                        'www.allianzrealestate.com',
                        'www.allianzrealestate.de',
                        'www.allianzrealestategermany.com',
                    ],
                    target: '185.136.112.166',
                },
                {
                    forward_reverse_dns: [
                        'allianz-fuer-die-jugend.de',
                        'allianz-kinderstiftung.de',
                        'allianzblau.de',
                        'allianzdeutschland.de',
                        'autoconfig.webmail.allianzblau.de',
                        'autodiscover.webmail.allianzblau.de',
                        'ftp.allianz-fuer-die-jugend.de',
                        'ftp.allianz-kinderstiftung.de',
                        'ftp.allianzblau.de',
                        'webdisk.webmail.allianzblau.de',
                        'www.allianz-fuer-die-jugend.de',
                        'www.allianz-kinderstiftung.de',
                        'www.allianzblau.de',
                        'www.allianzdeutschland.de',
                        'www.webmail.allianzblau.de',
                    ],
                    target: '185.136.112.20',
                },
                {
                    forward_reverse_dns: [
                        'mail.allianz-fuer-die-jugend.de',
                        'mail.allianz-kinderstiftung.de',
                        'skhost202.hosting.it-servicecenter.com',
                    ],
                    target: '185.136.112.22',
                },
                {
                    forward_reverse_dns: [
                        'allianz-real-estate.com',
                        'allianz-real-estate.de',
                        'allianzrealestate.de',
                        'neos.allianz-realestate.com',
                    ],
                    target: '185.136.112.74',
                },
                {
                    forward_reverse_dns: [
                        'autoconfig.allianzlifechanger.com',
                        'autodiscover.allianzlifechanger.com',
                        'url-fwd.easydns.com',
                    ],
                    target: '185.169.253.175',
                },
                {
                    forward_reverse_dns: [
                        'www.riskdirector.co.uk',
                        '185.182.90.109.srvlist.ukfast.net',
                    ],
                    target: '185.182.90.109',
                },
                {
                    forward_reverse_dns: [
                        'azt-automotive.com',
                        'azt.allianz.de',
                        'industry.azt-automotive.com',
                        'next.azt-automotive.com',
                        'www.azt-automotive.com',
                        'www.azt.allianz.com',
                    ],
                    target: '185.237.67.118',
                },
                {
                    forward_reverse_dns: [
                        'm.central.sk',
                        'mapa.central.sk',
                        'najemci.central.sk',
                        'mail.smarcoms.cz',
                    ],
                    target: '185.58.41.64',
                },
                {
                    forward_reverse_dns: [
                        'blog.allianz-assistance.be',
                        'blog.allianz-assistance.nl',
                    ],
                    target: '185.87.249.253',
                },
                {
                    forward_reverse_dns: [
                        'news.agaportal.de',
                        'pascal.dialogue1.de',
                    ],
                    target: '188.138.81.132',
                },
                {
                    forward_reverse_dns: [
                        'ftp.sepsad-telesurveillance.fr',
                        'sepsad-telesurveillance.fr',
                        'www.sepsad-telesurveillance.fr',
                        'www.sepsadsecurite.com',
                        'www.sepsadsecurite.fr',
                    ],
                    target: '188.165.170.161',
                },
                {
                    forward_reverse_dns: [
                        'ce-postmaster.de',
                        'cepostmaster.de',
                        'www.www2.controlexpert.de',
                        'www2.controlexpert.de',
                    ],
                    target: '188.94.254.116',
                },
                {
                    forward_reverse_dns: [
                        'beitragsanalyse.de',
                        'ids-akademie.com',
                        'ids-forum.co.uk',
                        'ids-forum.com',
                        'investmentdataservices.asia',
                        'investmentdataservices.de',
                        'investmentdataservices.eu',
                        'investmentdataservices.net',
                        'performanceattribution.de',
                        'www.beitragsanalyse.de',
                        'www.ids-akademie.com',
                        'www.ids-forum.co.uk',
                        'www.ids-forum.com',
                        'www.investmentdataservices.asia',
                        'www.investmentdataservices.de',
                        'www.investmentdataservices.eu',
                        'www.investmentdataservices.net',
                        'www.performanceattribution.de',
                    ],
                    target: '188.94.254.147',
                },
                {
                    forward_reverse_dns: [
                        'dev.investmentdataservices.info',
                        'ids-academy.com',
                        'www.ids-academy.com',
                    ],
                    target: '188.94.254.81',
                },
                {
                    forward_reverse_dns: [
                        'allianzlankalife.com',
                        'www.allianzlankalife.com',
                        'ip-192-186-219-201.ip.secureserver.net',
                    ],
                    target: '192.186.219.201',
                },
                {
                    forward_reverse_dns: [
                        'showroom.allianz.com',
                        'c13.aboliton.at',
                    ],
                    target: '193.200.113.146',
                },
                {
                    forward_reverse_dns: [
                        'cageur-uat.eulerhermes.com',
                        'gitlab.eulerhermes.com',
                    ],
                    target: '193.29.52.231',
                },
                {
                    forward_reverse_dns: [
                        'spark.allianz.fr',
                    ],
                    target: '193.41.223.131',
                },
                {
                    forward_reverse_dns: [
                        'ftp.allianz-assistance.com',
                        'ftp.mondial-assistance.com',
                    ],
                    target: '193.58.223.76',
                },
                {
                    forward_reverse_dns: [
                        'ftp.allianz-suisse.ch',
                        'ftp.allianz-suisse.li',
                        'ftp.allianz-swiss.ch',
                        'ftp.allianz.ch',
                        'ftp.allianzgroup.ch',
                        'ftp.allianzsuisse.ch',
                        'ftp.allianzsuisse.li',
                        'ftp.allianzswiss.ch',
                        'ftp.art-allianz.ch',
                        'ftp.azgrp.ch',
                        'ftp.cap.ch',
                        'sftp.allianz-suisse.ch',
                    ],
                    target: '193.8.109.150',
                },
                {
                    forward_reverse_dns: [
                        'smtp-edge1.amer1.mail.service.allianz.com',
                    ],
                    target: '194.127.21.34',
                },
                {
                    forward_reverse_dns: [
                        'smtp-edge2.amer1.mail.service.allianz.com',
                    ],
                    target: '194.127.21.35',
                },
                {
                    forward_reverse_dns: [
                        'hogar.multiasistencia.com',
                        'multiasistencia.com',
                        'multiassistance.com',
                        'multiassistance.fr',
                        'www.multiasistencia.com',
                        'www.multiassistance.com',
                        'www.multiassistance.fr',
                        'ws19414059109.fujitsu.es',
                    ],
                    target: '194.140.59.109',
                },
                {
                    forward_reverse_dns: [
                        'pre.sigmareparaciones.com',
                        'sigmareparaciones.com',
                        'travauxsanssoucis.fr',
                        'www.sigmareparaciones.com',
                        'tiendaempresas.unicef.es',
                    ],
                    target: '194.140.59.8',
                },
                {
                    forward_reverse_dns: [],
                    target: '194.162.245.58',
                },
                {
                    forward_reverse_dns: [],
                    target: '194.162.245.62',
                },
                {
                    forward_reverse_dns: [
                        'admin.aon-allianz24.ch',
                        'aon-allianz24.ch',
                        'autoconfig.aon-allianz24.ch',
                        'autodiscover.aon-allianz24.ch',
                        'ftp.aon-allianz24.ch',
                        'm.aon-allianz24.ch',
                        'mail.aon-allianz24.ch',
                        'webmail.aon-allianz24.ch',
                        'www.aon-allianz24.ch',
                        'mx01.iteconomy.ch',
                    ],
                    target: '195.201.108.172',
                },
                {
                    forward_reverse_dns: [
                        'ftps.control-expert.de',
                        'ftps.controlexpert.com',
                        'ftps.controlexpert.eu',
                        'ip-195-216-235-121.reverse.controlexpert.eu',
                        'sftp.controlexpert.com',
                        'sftp.controlexpert.eu',
                    ],
                    target: '195.216.235.121',
                },
                {
                    forward_reverse_dns: [
                        'ftp.control-expert.de',
                        'ftp.controlexpert.de',
                        'ftp.glas-expert.de',
                        'ftp.glas-experte.de',
                        'ftp.glas-experten.de',
                        'ftp.glasexpert.de',
                        'ftp.glasexperten.de',
                        'ftp.gutachten-vermittlung.de',
                        'ftp.gutachtenvermittlung.de',
                        'ip-195-216-235-70.reverse.controlexpert.eu',
                        'sftp.control-expert.de',
                        'sftp.controlexpert.de',
                        'www.ftp.controlexpert.de',
                    ],
                    target: '195.216.235.70',
                },
                {
                    forward_reverse_dns: [
                        'ftp.controlexpert.com',
                        'ftp.controlexpert.eu',
                        'ip-195-216-235-71.reverse.controlexpert.eu',
                        'www.ftp.controlexpert.com',
                    ],
                    target: '195.216.235.71',
                },
                {
                    forward_reverse_dns: [
                        'sectrans1.allianz.at',
                        'sectrans2.allianz.at',
                    ],
                    target: '195.245.92.196',
                },
                {
                    forward_reverse_dns: [
                        'test.my-admin.agaportal.de',
                        'test.my-drive.agaportal.de',
                        'test.my.agaportal.de',
                        'my.agaportal.de',
                    ],
                    target: '195.252.147.34',
                },
                {
                    forward_reverse_dns: [
                        'staging.my-admin.agaportal.de',
                        'staging.my-drive.agaportal.de',
                        'staging.my.agaportal.de',
                        'my.agaportal.de',
                    ],
                    target: '195.252.147.35',
                },
                {
                    forward_reverse_dns: [
                        'my-admin.agaportal.de',
                        'my-drive.agaportal.de',
                        'my.agaportal.de',
                    ],
                    target: '195.252.147.36',
                },
                {
                    forward_reverse_dns: [
                        'staging.agaportal.de',
                        '38-147-252-195.psmanaged.com',
                    ],
                    target: '195.252.147.38',
                },
                {
                    forward_reverse_dns: [
                        'ee2.fi',
                        'www.ee2.fi',
                        'vm60217.psmanaged.com',
                    ],
                    target: '195.252.155.194',
                },
                {
                    forward_reverse_dns: [
                        'sparkformation.allianz.fr',
                    ],
                    target: '195.254.147.21',
                },
                {
                    forward_reverse_dns: [],
                    target: '195.52.230.100',
                },
                {
                    forward_reverse_dns: [],
                    target: '195.63.118.100',
                },
                {
                    forward_reverse_dns: [
                        'gate.allianzdirect.ro',
                    ],
                    target: '195.82.149.7',
                },
                {
                    forward_reverse_dns: [
                        'ftp.investitori.it',
                        'hosting.debsolutions.net',
                    ],
                    target: '195.88.6.161',
                },
                {
                    forward_reverse_dns: [
                        'travel.allianz.ng',
                        'www57.cpt3.host-h.net',
                    ],
                    target: '197.221.14.57',
                },
                {
                    forward_reverse_dns: [],
                    target: '197.253.33.70',
                },
                {
                    forward_reverse_dns: [
                        'sft.bajajallianz.com',
                    ],
                    target: '202.90.192.62',
                },
                {
                    forward_reverse_dns: [
                        'direct.azay.co.th',
                        '203-150-58-220.inter.net.th',
                    ],
                    target: '203.150.58.220',
                },
                {
                    forward_reverse_dns: [
                        'smtp-edge1.amer2.mail.service.allianz.com',
                    ],
                    target: '205.167.40.34',
                },
                {
                    forward_reverse_dns: [
                        'smtp-edge2.amer2.mail.service.allianz.com',
                    ],
                    target: '205.167.40.35',
                },
                {
                    forward_reverse_dns: [
                        'mfttest.allianzlife.com',
                    ],
                    target: '205.167.71.161',
                },
                {
                    forward_reverse_dns: [
                        'mft.allianzlife.com',
                    ],
                    target: '205.167.71.164',
                },
                {
                    forward_reverse_dns: [],
                    target: '206.189.72.192',
                },
                {
                    forward_reverse_dns: [
                        'myinforcesolutions.com',
                        'www.myinforcesolutions.com',
                        'cloudhost-1920786.us-midwest-1.nxcli.net',
                    ],
                    target: '209.87.159.56',
                },
                {
                    forward_reverse_dns: [],
                    target: '212.121.145.131',
                },
                {
                    forward_reverse_dns: [],
                    target: '212.121.145.132',
                },
                {
                    forward_reverse_dns: [
                        'd104.x-mailer.de',
                    ],
                    target: '212.162.12.130',
                },
                {
                    forward_reverse_dns: [
                        'www.multicorreduria.com',
                    ],
                    target: '212.227.205.110',
                },
                {
                    forward_reverse_dns: [
                        'vs1235.mymanaged.host',
                    ],
                    target: '212.83.50.232',
                },
                {
                    forward_reverse_dns: [],
                    target: '213.95.157.162',
                },
                {
                    forward_reverse_dns: [
                        'news.darta.ie',
                        'www.idc.mn',
                    ],
                    target: '217.112.89.36',
                },
                {
                    forward_reverse_dns: [
                        'ip16.ip-217-182-250.eu',
                    ],
                    target: '217.182.250.16',
                },
                {
                    forward_reverse_dns: [
                        'eshop.eulerhermes.com',
                        'pclagardere1.772424.hosting',
                    ],
                    target: '217.182.34.129',
                },
                {
                    forward_reverse_dns: [
                        'eurogarantie.ch',
                        'garantie.ch',
                        'quality-1.ch',
                        'quality1.ch',
                        'qualityonedirect.swiss',
                        'www.eurogarantie.ch',
                        'www.quality-1.ch',
                        'www.quality1.ch',
                        'sl335.web.hostpoint.ch',
                    ],
                    target: '217.26.51.230',
                },
                {
                    forward_reverse_dns: [
                        'www.bajajallianz.co.in',
                        'www.bajajallianz.in',
                    ],
                    target: '23.100.91.41',
                },
                {
                    forward_reverse_dns: [
                        'entsia.charts.entsia.io',
                        'entsia.rancher.entsia.io',
                        'ec2-3-106-109-59.ap-southeast-2.compute.amazonaws.com',
                    ],
                    target: '3.106.109.59',
                },
                {
                    forward_reverse_dns: [
                        'ec2-3-239-175-55.compute-1.amazonaws.com',
                    ],
                    target: '3.239.175.55',
                },
                {
                    forward_reverse_dns: [
                        'ec2-3-24-104-78.ap-southeast-2.compute.amazonaws.com',
                    ],
                    target: '3.24.104.78',
                },
                {
                    forward_reverse_dns: [
                        'cmi.allianzsna.com',
                        'ec2-34-204-59-191.compute-1.amazonaws.com',
                    ],
                    target: '34.204.59.191',
                },
                {
                    forward_reverse_dns: [
                        'videosupport.bajajallianzlife.com',
                        'ec2-35-154-6-248.ap-south-1.compute.amazonaws.com',
                    ],
                    target: '35.154.6.248',
                },
                {
                    forward_reverse_dns: [
                        'puppycentre.petplan.co.uk',
                        'www.puppycentre.petplan.co.uk',
                        '112.110.189.35.bc.googleusercontent.com',
                    ],
                    target: '35.189.110.112',
                },
                {
                    forward_reverse_dns: [
                        'ezlavy.central.sk',
                        '3.175.9.37.in-addr.arpa.websupport.sk',
                    ],
                    target: '37.9.175.3',
                },
                {
                    forward_reverse_dns: [
                        'allianz-togo.com',
                        'www.allianz-togo.com',
                    ],
                    target: '43.129.189.89',
                },
                {
                    forward_reverse_dns: [],
                    target: '43.245.181.96',
                },
                {
                    forward_reverse_dns: [
                        'm.rueeuler.com',
                        'rueeuler.com',
                        'www.rueeuler.com',
                    ],
                    target: '45.15.126.145',
                },
                {
                    forward_reverse_dns: [
                        'askforallianz.com',
                        'www.askforallianz.com',
                        'ip-45-40-149-34.ip.secureserver.net',
                    ],
                    target: '45.40.149.34',
                },
                {
                    forward_reverse_dns: [
                        'mail.allianz-riviera.fr',
                        'ns384263.ovh.net',
                    ],
                    target: '46.105.122.44',
                },
                {
                    forward_reverse_dns: [
                        'ip74.ip-46-105-38.eu',
                    ],
                    target: '46.105.38.74',
                },
                {
                    forward_reverse_dns: [
                        'mdm.ampersand-gmbh.de',
                    ],
                    target: '46.163.76.73',
                },
                {
                    forward_reverse_dns: [
                        'eulerhermes-azubiblog.de',
                        'www.eulerhermes-azubiblog.de',
                        'scrat.ispgateway.de',
                    ],
                    target: '46.252.18.127',
                },
                {
                    forward_reverse_dns: [
                        'animaux-sante.fr',
                        'astuce-credit.fr',
                        'mes-meilleures-assurances.fr',
                        'un-meilleur-credit.fr',
                        'pikok.ispgateway.de',
                    ],
                    target: '46.252.18.154',
                },
                {
                    forward_reverse_dns: [
                        'allianz-autoversicherung.de',
                        'allianz-unter-strom.de',
                        'allianzunterstrom.de',
                        'crash-recorder.de',
                        'kfz-versicherung-allianz.de',
                        'www.allianz-autoversicherung.de',
                        'www.allianz-unter-strom.de',
                        'www.allianzunterstrom.de',
                        'www.crash-recorder.de',
                        'www.kfz-versicherung-allianz.de',
                    ],
                    target: '46.30.56.91',
                },
                {
                    forward_reverse_dns: [
                        'www.pkw-steuerservice.de',
                    ],
                    target: '46.30.57.9',
                },
                {
                    forward_reverse_dns: [
                        'content.allianztuitioninsurance.com',
                        'ip-50-62-194-30.ip.secureserver.net',
                    ],
                    target: '50.62.194.30',
                },
                {
                    forward_reverse_dns: [
                        'constat.allianz.fr',
                        'devconstat.allianz.fr',
                        'statsconstat.allianz.fr',
                        'www.constat.allianz.fr',
                        '234.ip-51-178-82.eu',
                    ],
                    target: '51.178.82.234',
                },
                {
                    forward_reverse_dns: [
                        'srv412.e-doceo.com',
                    ],
                    target: '51.178.89.118',
                },
                {
                    forward_reverse_dns: [
                        'eannu.com',
                        'www.eannu.com',
                        '132.ip-51-254-212.eu',
                    ],
                    target: '51.254.212.132',
                },
                {
                    forward_reverse_dns: [
                        'ftp.kaiser-x.com',
                        'old.ssh.cluster002.hosting.ovh.net',
                    ],
                    target: '51.68.11.188',
                },
                {
                    forward_reverse_dns: [
                        'ftp.diagnostic-fraude-entreprise.com',
                        'ftp.cluster005.hosting.ovh.net',
                    ],
                    target: '51.68.11.196',
                },
                {
                    forward_reverse_dns: [
                        'ftp.allianz-ecogestes.fr',
                        'ftp.cluster007.hosting.ovh.net',
                    ],
                    target: '51.68.11.204',
                },
                {
                    forward_reverse_dns: [
                        'mail.uk.w3pcloud.com',
                    ],
                    target: '54.76.40.173',
                },
                {
                    forward_reverse_dns: [
                        'vs5425.mymanaged.host',
                    ],
                    target: '62.113.218.4',
                },
                {
                    forward_reverse_dns: [
                        'aam.de',
                        'vermoegensmanagement.de',
                        'www.vermoegensmanagement.de',
                        'www.seg-host.de',
                    ],
                    target: '62.116.165.203',
                },
                {
                    forward_reverse_dns: [
                        'arelive.com',
                        'www.arelive.com',
                        'vhosts.noris.net',
                    ],
                    target: '62.128.1.80',
                },
                {
                    forward_reverse_dns: [
                        'goglobal.allianz.com',
                        'rz-1.icunet.ag',
                    ],
                    target: '62.146.7.238',
                },
                {
                    forward_reverse_dns: [
                        'meet.awo-in-sachsen.de',
                    ],
                    target: '62.225.148.180',
                },
                {
                    forward_reverse_dns: [],
                    target: '62.26.245.30',
                },
                {
                    forward_reverse_dns: [],
                    target: '62.26.245.34',
                },
                {
                    forward_reverse_dns: [
                        '66-206-3-38.static.hvvc.us',
                    ],
                    target: '66.206.3.38',
                },
                {
                    forward_reverse_dns: [
                        'myaccount.allianztravelinsurance.com',
                        'aamoigcacq.c05.gridserver.com',
                    ],
                    target: '70.32.69.173',
                },
                {
                    forward_reverse_dns: [
                        'filetransfer.allianz-assistance.ca',
                        'filetransfer.travelinsurance.ca',
                        'unallocated-static.rogers.com',
                    ],
                    target: '72.142.65.148',
                },
                {
                    forward_reverse_dns: [
                        'ftp.mmagrar.info',
                        'imap.mmagrar.info',
                        'mail.mmagrar.info',
                        'mmagrar.de',
                        'mmagrar.info',
                        'new.mmagrar.info',
                        'pop.mmagrar.info',
                        'portal.mmagrar.info',
                        'relay.mmagrar.info',
                        'smtp.mmagrar.info',
                        'www.mmagrar.de',
                        'www.mmagrar.info',
                        'www.portal.mmagrar.info',
                        'www377.your-server.de',
                    ],
                    target: '78.46.152.62',
                },
                {
                    forward_reverse_dns: [
                        'mail.mawista.de',
                        'mail.mawista.info',
                        'mail.mawista.org',
                        'dedi2970.your-server.de',
                    ],
                    target: '78.46.155.156',
                },
                {
                    forward_reverse_dns: [
                        'www280.your-server.de',
                    ],
                    target: '78.47.69.36',
                },
                {
                    forward_reverse_dns: [],
                    target: '80.122.63.122',
                },
                {
                    forward_reverse_dns: [
                        'imap.allianz-ci.com',
                        'imap.allianz-cm.com',
                        'imap.allianz-mg.com',
                        'imap.allianz-sn.com',
                        'pop.allianz-ci.com',
                        'pop.allianz-cm.com',
                        'pop.allianz-mg.com',
                        'pop.allianz-sn.com',
                        'perdition-green.nfrance.com',
                    ],
                    target: '80.247.228.30',
                },
                {
                    forward_reverse_dns: [
                        'imap.allianz-ci.com',
                        'imap.allianz-cm.com',
                        'imap.allianz-mg.com',
                        'imap.allianz-sn.com',
                        'pop.allianz-ci.com',
                        'pop.allianz-cm.com',
                        'pop.allianz-mg.com',
                        'pop.allianz-sn.com',
                        'perdition-blue.nfrance.com',
                    ],
                    target: '80.247.229.28',
                },
                {
                    forward_reverse_dns: [
                        'grid1c.sigmatic.fi',
                    ],
                    target: '80.69.161.36',
                },
                {
                    forward_reverse_dns: [
                        'boutiquevignobleslarose.fr',
                        'vignoblesdelarose.fr',
                        'impec238-03.aqserver.net',
                    ],
                    target: '80.93.93.206',
                },
                {
                    forward_reverse_dns: [
                        'old.medexpress.ru',
                        'srv157-h-st.jino.ru',
                    ],
                    target: '81.177.135.110',
                },
                {
                    forward_reverse_dns: [
                        'eulerhermes.netmon2.technacy.it',
                    ],
                    target: '81.29.205.112',
                },
                {
                    forward_reverse_dns: [
                        'gin.allianz.com',
                        'lvps83-169-47-190.dedicated.hosteurope.de',
                    ],
                    target: '83.169.47.190',
                },
                {
                    forward_reverse_dns: [
                        'dev.justis.ch',
                        'h2847439.stratoserver.net',
                    ],
                    target: '85.214.219.55',
                },
                {
                    forward_reverse_dns: [
                        'sftp.multiasistencia.com',
                        'sftp.multiassistance.com',
                        'sftp.multiassistance.fr',
                        'sftp.multiassistentie.com',
                        '82.98.62.85.in-addr.arpa',
                    ],
                    target: '85.62.98.82',
                },
                {
                    forward_reverse_dns: [],
                    target: '87.106.126.122',
                },
                {
                    forward_reverse_dns: [
                        'test.meinetiroler.at',
                        'www-old.meinetiroler.at',
                        'www-old.meinetiroler.it',
                        'www.test.meinetiroler.at',
                        'www.test.meinetiroler.it',
                        'static.88-198-201-45.clients.your-server.de',
                    ],
                    target: '88.198.201.45',
                },
                {
                    forward_reverse_dns: [],
                    target: '89.149.166.61',
                },
                {
                    forward_reverse_dns: [
                        'admin.mondial-assistance-italia.it',
                        'previndustria.it',
                        'www.previndustria.it',
                        'es1.espero.it',
                    ],
                    target: '89.186.95.213',
                },
                {
                    forward_reverse_dns: [
                        'support.vignoblesdelarose.com',
                        'ecs-90-84-191-167.compute.prod-cloud-ocb.orange-business.com',
                    ],
                    target: '90.84.191.167',
                },
                {
                    forward_reverse_dns: [
                        'oficinas.allianz.pt',
                        'redeclinica.allianz.pt',
                        'redeclinicaacpessoais.allianz.pt',
                        'redeclinicaactrabalho.allianz.pt',
                        'vr14.variograma.net',
                    ],
                    target: '92.48.69.171',
                },
                {
                    forward_reverse_dns: [
                        'ersatzkasse.ch',
                        'www.ersatzkasse.ch',
                    ],
                    target: '94.126.20.68',
                },
                {
                    forward_reverse_dns: [
                        'bagicin.com',
                        'ftp.bagicin.com',
                        'www.bagicin.com',
                        'static.118.12.130.94.clients.your-server.de',
                    ],
                    target: '94.130.12.118',
                },
                {
                    forward_reverse_dns: [
                        'agrarversicherung.com',
                        'www.agrarversicherung.com',
                        'v22018056266366437.quicksrv.de',
                    ],
                    target: '94.16.117.80',
                },
                {
                    forward_reverse_dns: [
                        'medlar.sectio-aurea.org',
                    ],
                    target: '94.16.120.113',
                },
                {
                    forward_reverse_dns: [
                        '_collab-edge._tls.nextcarehealth.com',
                        '_sips._tcp.nextcarehealth.com',
                        'nc-expe.nextcarehealth.com',
                    ],
                    target: '94.56.15.26',
                },
            ],
            effort: 1,
            hackability_share: 1.8173557473875512,
            issues: [
                {
                    accepted_on: null,
                    cves: null,
                    fixed_on: null,
                    hosts: [
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '1.253.240.3',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'gtins.com.au',
                                'www.gtins.com.au',
                                '174.111.0.101.static.digitalpacific.com.au',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '101.0.111.174',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'primacywww.pum.co.nz',
                                'primacywww.pum.com.au',
                                'tommy-alias.bnr.la',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '103.1.186.60',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'cpanel.agamedicalnetwork.com',
                                'mail.agamedicalnetwork.com',
                                'webdisk.agamedicalnetwork.com',
                                'webmail.agamedicalnetwork.com',
                                'whm.agamedicalnetwork.com',
                                'md-in-36.webhostbox.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '103.21.58.29',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'cpanel.agamedicalnetwork.com',
                                'mail.agamedicalnetwork.com',
                                'webdisk.agamedicalnetwork.com',
                                'webmail.agamedicalnetwork.com',
                                'whm.agamedicalnetwork.com',
                                'md-in-36.webhostbox.net',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '103.21.58.29',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'link.eh-dach.de',
                                '78.54.154.104.bc.googleusercontent.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '104.154.54.78',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ip-107-180-41-243.ip.secureserver.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '107.180.41.243',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'admin.eolisguide.com',
                                'autoconfig.admin.eolisguide.com',
                                'autoconfig.eolisguide.com',
                                'autodiscover.admin.eolisguide.com',
                                'autodiscover.eolisguide.com',
                                'cpanel.eolisguide.com',
                                'eolisguide.com',
                                'ftp.eolisguide.com',
                                'mail.eolisguide.com',
                                'webdisk.admin.eolisguide.com',
                                'webdisk.eolisguide.com',
                                'webmail.eolisguide.com',
                                'whm.eolisguide.com',
                                'www.admin.eolisguide.com',
                                'www.eolisguide.com',
                                'ip-107-180-50-234.ip.secureserver.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '107.180.50.234',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'apidev.allianz.com.my',
                            ],
                            port: 5001,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '110.74.163.72',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'apidev.allianz.com.my',
                            ],
                            port: 5002,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '110.74.163.72',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'apidev.allianz.com.my',
                            ],
                            port: 5003,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '110.74.163.72',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'apidev.allianz.com.my',
                            ],
                            port: 5004,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '110.74.163.72',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'apidev.allianz.com.my',
                            ],
                            port: 5009,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '110.74.163.72',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'oneglobalshop.allianzgi.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '116.202.201.35',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'oneglobalshop.allianzgi.com',
                            ],
                            port: 222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '116.202.201.35',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'allianz-x.com',
                                'allianz-x.de',
                                'www.allianz-x.com',
                                'www.allianz-x.de',
                                'steinrein1.timmeserver.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '116.202.231.184',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '12.234.93.62',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'h2web217.infomaniak.ch',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '128.65.195.127',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'veranstaltungshaftpflicht.allianz.de',
                                'cw22.virtualhosts.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '130.255.79.74',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'credit-bleu.fr',
                                'mon-rachat-de-credit.fr',
                                'jazz.ispgateway.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '134.119.45.85',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '134.213.212.77',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '138.197.103.178',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ip4.ip-142-4-223.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '142.4.223.4',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'www.medienplan.biz',
                                'www.medienplan.com',
                                'www.medienplan.info',
                                'ajtakreview.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '144.172.64.31',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'adamasiapac.com',
                                'www.adamasiapac.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '154.201.160.53',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'controlexpert.com',
                                'international.ce-systeme.de',
                                'test1.ce-systeme.de',
                                'test2.ce-systeme.de',
                                'web-cdn.controlexpert.com',
                                'www.controlexpert.com',
                                'www.test1.ce-systeme.de',
                                'www.test2.ce-systeme.de',
                                'www.www2.controlexpert.com',
                                'www2.controlexpert.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '156.67.236.104',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'allianzx.com',
                                'www.allianzx.com',
                                'werkraum1.timmeserver.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '157.90.212.141',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'newsletter.borgosanfelice.it',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '159.89.101.21',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mail.hospeda.mx',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '165.227.43.209',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'sftp.roggeglobal.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '168.114.112.34',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'd1.02.33a9.ip4.static.sl-reverse.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '169.51.2.209',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '171.22.127.229',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.mondial-projects.com',
                                'www.mondial-projects.com',
                                'ip-173-201-254-177.ip.secureserver.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '173.201.254.177',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'investmentdataservices.info',
                                'www.investmentdataservices.info',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '176.52.243.57',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'business-176-094-147-058.static.arcor-ip.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '176.94.147.58',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'jaideunprochedependant.fr',
                                'www.jaideunprochedependant.fr',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '178.32.17.45',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'lvps178-77-101-114.dedicated.hosteurope.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '178.77.101.114',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'allianz-arbeitssicherheit.de',
                                'allianz-learning.com',
                                'allianzseminare.de',
                                'www.allianz-learning.com',
                                'www.allianzseminare.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '178.77.89.84',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.allianzatoz.com',
                                'ftp.allianzblue.com',
                                'ftp.allianzincomefocus.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '184.106.55.230',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'berner.ch',
                                'mail.policencheck.ch',
                                'mail.policencheck.li',
                                'mail.splitsurance.ch',
                                'mail.splitsurance.com',
                                'mail.splitsurance.li',
                                'policencheck.ch',
                                'policencheck.li',
                                'splitsurance.ch',
                                'splitsurance.com',
                                'splitsurance.li',
                                'www-smtp.splitsurance.ch',
                                'www.splitsurance.ch',
                                'www.splitsurance.com',
                                'www.splitsurance.li',
                                'wwwq.splitsurance.ch',
                                'default.hosttech.eu',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.101.158.113',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'allianz-realestate.com',
                                'www.allianz-real-estate.com',
                                'www.allianz-real-estate.de',
                                'www.allianz-realestate.com',
                                'www.allianzrealestate.com',
                                'www.allianzrealestate.de',
                                'www.allianzrealestategermany.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.136.112.166',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'allianz-fuer-die-jugend.de',
                                'allianz-kinderstiftung.de',
                                'allianzblau.de',
                                'allianzdeutschland.de',
                                'autoconfig.webmail.allianzblau.de',
                                'autodiscover.webmail.allianzblau.de',
                                'ftp.allianz-fuer-die-jugend.de',
                                'ftp.allianz-kinderstiftung.de',
                                'ftp.allianzblau.de',
                                'webdisk.webmail.allianzblau.de',
                                'www.allianz-fuer-die-jugend.de',
                                'www.allianz-kinderstiftung.de',
                                'www.allianzblau.de',
                                'www.allianzdeutschland.de',
                                'www.webmail.allianzblau.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.136.112.20',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mail.allianz-fuer-die-jugend.de',
                                'mail.allianz-kinderstiftung.de',
                                'skhost202.hosting.it-servicecenter.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.136.112.22',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'allianz-real-estate.com',
                                'allianz-real-estate.de',
                                'allianzrealestate.de',
                                'neos.allianz-realestate.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.136.112.74',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'autoconfig.allianzlifechanger.com',
                                'autodiscover.allianzlifechanger.com',
                                'url-fwd.easydns.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.169.253.175',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'www.riskdirector.co.uk',
                                '185.182.90.109.srvlist.ukfast.net',
                            ],
                            port: 2020,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.182.90.109',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'azt-automotive.com',
                                'azt.allianz.de',
                                'industry.azt-automotive.com',
                                'next.azt-automotive.com',
                                'www.azt-automotive.com',
                                'www.azt.allianz.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.237.67.118',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'm.central.sk',
                                'mapa.central.sk',
                                'najemci.central.sk',
                                'mail.smarcoms.cz',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.58.41.64',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'blog.allianz-assistance.be',
                                'blog.allianz-assistance.nl',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '185.87.249.253',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'news.agaportal.de',
                                'pascal.dialogue1.de',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '188.138.81.132',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.sepsad-telesurveillance.fr',
                                'sepsad-telesurveillance.fr',
                                'www.sepsad-telesurveillance.fr',
                                'www.sepsadsecurite.com',
                                'www.sepsadsecurite.fr',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '188.165.170.161',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ce-postmaster.de',
                                'cepostmaster.de',
                                'www.www2.controlexpert.de',
                                'www2.controlexpert.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '188.94.254.116',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'beitragsanalyse.de',
                                'ids-akademie.com',
                                'ids-forum.co.uk',
                                'ids-forum.com',
                                'investmentdataservices.asia',
                                'investmentdataservices.de',
                                'investmentdataservices.eu',
                                'investmentdataservices.net',
                                'performanceattribution.de',
                                'www.beitragsanalyse.de',
                                'www.ids-akademie.com',
                                'www.ids-forum.co.uk',
                                'www.ids-forum.com',
                                'www.investmentdataservices.asia',
                                'www.investmentdataservices.de',
                                'www.investmentdataservices.eu',
                                'www.investmentdataservices.net',
                                'www.performanceattribution.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '188.94.254.147',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'dev.investmentdataservices.info',
                                'ids-academy.com',
                                'www.ids-academy.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '188.94.254.81',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'allianzlankalife.com',
                                'www.allianzlankalife.com',
                                'ip-192-186-219-201.ip.secureserver.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '192.186.219.201',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'showroom.allianz.com',
                                'c13.aboliton.at',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '193.200.113.146',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'cageur-uat.eulerhermes.com',
                                'gitlab.eulerhermes.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '193.29.52.231',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'spark.allianz.fr',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '193.41.223.131',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.allianz-assistance.com',
                                'ftp.mondial-assistance.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '193.58.223.76',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.allianz-suisse.ch',
                                'ftp.allianz-suisse.li',
                                'ftp.allianz-swiss.ch',
                                'ftp.allianz.ch',
                                'ftp.allianzgroup.ch',
                                'ftp.allianzsuisse.ch',
                                'ftp.allianzsuisse.li',
                                'ftp.allianzswiss.ch',
                                'ftp.art-allianz.ch',
                                'ftp.azgrp.ch',
                                'ftp.cap.ch',
                                'sftp.allianz-suisse.ch',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '193.8.109.150',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'smtp-edge1.amer1.mail.service.allianz.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '194.127.21.34',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'smtp-edge2.amer1.mail.service.allianz.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '194.127.21.35',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'hogar.multiasistencia.com',
                                'multiasistencia.com',
                                'multiassistance.com',
                                'multiassistance.fr',
                                'www.multiasistencia.com',
                                'www.multiassistance.com',
                                'www.multiassistance.fr',
                                'ws19414059109.fujitsu.es',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '194.140.59.109',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'pre.sigmareparaciones.com',
                                'sigmareparaciones.com',
                                'travauxsanssoucis.fr',
                                'www.sigmareparaciones.com',
                                'tiendaempresas.unicef.es',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '194.140.59.8',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 830,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '194.162.245.58',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 830,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '194.162.245.62',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'admin.aon-allianz24.ch',
                                'aon-allianz24.ch',
                                'autoconfig.aon-allianz24.ch',
                                'autodiscover.aon-allianz24.ch',
                                'ftp.aon-allianz24.ch',
                                'm.aon-allianz24.ch',
                                'mail.aon-allianz24.ch',
                                'webmail.aon-allianz24.ch',
                                'www.aon-allianz24.ch',
                                'mx01.iteconomy.ch',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.201.108.172',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'admin.aon-allianz24.ch',
                                'aon-allianz24.ch',
                                'autoconfig.aon-allianz24.ch',
                                'autodiscover.aon-allianz24.ch',
                                'ftp.aon-allianz24.ch',
                                'm.aon-allianz24.ch',
                                'mail.aon-allianz24.ch',
                                'webmail.aon-allianz24.ch',
                                'www.aon-allianz24.ch',
                                'mx01.iteconomy.ch',
                            ],
                            port: 5000,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.201.108.172',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftps.control-expert.de',
                                'ftps.controlexpert.com',
                                'ftps.controlexpert.eu',
                                'ip-195-216-235-121.reverse.controlexpert.eu',
                                'sftp.controlexpert.com',
                                'sftp.controlexpert.eu',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.216.235.121',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.control-expert.de',
                                'ftp.controlexpert.de',
                                'ftp.glas-expert.de',
                                'ftp.glas-experte.de',
                                'ftp.glas-experten.de',
                                'ftp.glasexpert.de',
                                'ftp.glasexperten.de',
                                'ftp.gutachten-vermittlung.de',
                                'ftp.gutachtenvermittlung.de',
                                'ip-195-216-235-70.reverse.controlexpert.eu',
                                'sftp.control-expert.de',
                                'sftp.controlexpert.de',
                                'www.ftp.controlexpert.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.216.235.70',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.controlexpert.com',
                                'ftp.controlexpert.eu',
                                'ip-195-216-235-71.reverse.controlexpert.eu',
                                'www.ftp.controlexpert.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.216.235.71',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'sectrans1.allianz.at',
                                'sectrans2.allianz.at',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.245.92.196',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'test.my-admin.agaportal.de',
                                'test.my-drive.agaportal.de',
                                'test.my.agaportal.de',
                                'my.agaportal.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.252.147.34',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'staging.my-admin.agaportal.de',
                                'staging.my-drive.agaportal.de',
                                'staging.my.agaportal.de',
                                'my.agaportal.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.252.147.35',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'my-admin.agaportal.de',
                                'my-drive.agaportal.de',
                                'my.agaportal.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.252.147.36',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'staging.agaportal.de',
                                '38-147-252-195.psmanaged.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.252.147.38',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ee2.fi',
                                'www.ee2.fi',
                                'vm60217.psmanaged.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.252.155.194',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'sparkformation.allianz.fr',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.254.147.21',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 830,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.52.230.100',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 830,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.63.118.100',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'gate.allianzdirect.ro',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.82.149.7',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.investitori.it',
                                'hosting.debsolutions.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '195.88.6.161',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'travel.allianz.ng',
                                'www57.cpt3.host-h.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '197.221.14.57',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'travel.allianz.ng',
                                'www57.cpt3.host-h.net',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '197.221.14.57',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '197.253.33.70',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'sft.bajajallianz.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '202.90.192.62',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'direct.azay.co.th',
                                '203-150-58-220.inter.net.th',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '203.150.58.220',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'smtp-edge1.amer2.mail.service.allianz.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '205.167.40.34',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'smtp-edge2.amer2.mail.service.allianz.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '205.167.40.35',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mfttest.allianzlife.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '205.167.71.161',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mft.allianzlife.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '205.167.71.164',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '206.189.72.192',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'myinforcesolutions.com',
                                'www.myinforcesolutions.com',
                                'cloudhost-1920786.us-midwest-1.nxcli.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '209.87.159.56',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '212.121.145.131',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '212.121.145.132',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'd104.x-mailer.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '212.162.12.130',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'www.multicorreduria.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '212.227.205.110',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'vs1235.mymanaged.host',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '212.83.50.232',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 443,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '213.95.157.162',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'news.darta.ie',
                                'www.idc.mn',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '217.112.89.36',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ip16.ip-217-182-250.eu',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '217.182.250.16',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'eshop.eulerhermes.com',
                                'pclagardere1.772424.hosting',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '217.182.34.129',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'eurogarantie.ch',
                                'garantie.ch',
                                'quality-1.ch',
                                'quality1.ch',
                                'qualityonedirect.swiss',
                                'www.eurogarantie.ch',
                                'www.quality-1.ch',
                                'www.quality1.ch',
                                'sl335.web.hostpoint.ch',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '217.26.51.230',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'www.bajajallianz.co.in',
                                'www.bajajallianz.in',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '23.100.91.41',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'entsia.charts.entsia.io',
                                'entsia.rancher.entsia.io',
                                'ec2-3-106-109-59.ap-southeast-2.compute.amazonaws.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '3.106.109.59',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ec2-3-239-175-55.compute-1.amazonaws.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '3.239.175.55',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ec2-3-24-104-78.ap-southeast-2.compute.amazonaws.com',
                            ],
                            port: 1022,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '3.24.104.78',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'cmi.allianzsna.com',
                                'ec2-34-204-59-191.compute-1.amazonaws.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '34.204.59.191',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'videosupport.bajajallianzlife.com',
                                'ec2-35-154-6-248.ap-south-1.compute.amazonaws.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '35.154.6.248',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'puppycentre.petplan.co.uk',
                                'www.puppycentre.petplan.co.uk',
                                '112.110.189.35.bc.googleusercontent.com',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '35.189.110.112',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ezlavy.central.sk',
                                '3.175.9.37.in-addr.arpa.websupport.sk',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '37.9.175.3',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'allianz-togo.com',
                                'www.allianz-togo.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '43.129.189.89',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '43.245.181.96',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'm.rueeuler.com',
                                'rueeuler.com',
                                'www.rueeuler.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '45.15.126.145',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'askforallianz.com',
                                'www.askforallianz.com',
                                'ip-45-40-149-34.ip.secureserver.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '45.40.149.34',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mail.allianz-riviera.fr',
                                'ns384263.ovh.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '46.105.122.44',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ip74.ip-46-105-38.eu',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '46.105.38.74',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mdm.ampersand-gmbh.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '46.163.76.73',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'eulerhermes-azubiblog.de',
                                'www.eulerhermes-azubiblog.de',
                                'scrat.ispgateway.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '46.252.18.127',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'animaux-sante.fr',
                                'astuce-credit.fr',
                                'mes-meilleures-assurances.fr',
                                'un-meilleur-credit.fr',
                                'pikok.ispgateway.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '46.252.18.154',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'allianz-autoversicherung.de',
                                'allianz-unter-strom.de',
                                'allianzunterstrom.de',
                                'crash-recorder.de',
                                'kfz-versicherung-allianz.de',
                                'www.allianz-autoversicherung.de',
                                'www.allianz-unter-strom.de',
                                'www.allianzunterstrom.de',
                                'www.crash-recorder.de',
                                'www.kfz-versicherung-allianz.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '46.30.56.91',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'www.pkw-steuerservice.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '46.30.57.9',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'content.allianztuitioninsurance.com',
                                'ip-50-62-194-30.ip.secureserver.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '50.62.194.30',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'constat.allianz.fr',
                                'devconstat.allianz.fr',
                                'statsconstat.allianz.fr',
                                'www.constat.allianz.fr',
                                '234.ip-51-178-82.eu',
                            ],
                            port: 54321,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '51.178.82.234',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'srv412.e-doceo.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '51.178.89.118',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'eannu.com',
                                'www.eannu.com',
                                '132.ip-51-254-212.eu',
                            ],
                            port: 3333,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '51.254.212.132',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.kaiser-x.com',
                                'old.ssh.cluster002.hosting.ovh.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '51.68.11.188',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.diagnostic-fraude-entreprise.com',
                                'ftp.cluster005.hosting.ovh.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '51.68.11.196',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.allianz-ecogestes.fr',
                                'ftp.cluster007.hosting.ovh.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '51.68.11.204',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mail.uk.w3pcloud.com',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '54.76.40.173',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'vs5425.mymanaged.host',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '62.113.218.4',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'aam.de',
                                'vermoegensmanagement.de',
                                'www.vermoegensmanagement.de',
                                'www.seg-host.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '62.116.165.203',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'arelive.com',
                                'www.arelive.com',
                                'vhosts.noris.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '62.128.1.80',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'goglobal.allianz.com',
                                'rz-1.icunet.ag',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '62.146.7.238',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'goglobal.allianz.com',
                                'rz-1.icunet.ag',
                            ],
                            port: 50002,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '62.146.7.238',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'meet.awo-in-sachsen.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '62.225.148.180',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 830,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '62.26.245.30',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 830,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '62.26.245.34',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                '66-206-3-38.static.hvvc.us',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '66.206.3.38',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'myaccount.allianztravelinsurance.com',
                                'aamoigcacq.c05.gridserver.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '70.32.69.173',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'filetransfer.allianz-assistance.ca',
                                'filetransfer.travelinsurance.ca',
                                'unallocated-static.rogers.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '72.142.65.148',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.mmagrar.info',
                                'imap.mmagrar.info',
                                'mail.mmagrar.info',
                                'mmagrar.de',
                                'mmagrar.info',
                                'new.mmagrar.info',
                                'pop.mmagrar.info',
                                'portal.mmagrar.info',
                                'relay.mmagrar.info',
                                'smtp.mmagrar.info',
                                'www.mmagrar.de',
                                'www.mmagrar.info',
                                'www.portal.mmagrar.info',
                                'www377.your-server.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '78.46.152.62',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ftp.mmagrar.info',
                                'imap.mmagrar.info',
                                'mail.mmagrar.info',
                                'mmagrar.de',
                                'mmagrar.info',
                                'new.mmagrar.info',
                                'pop.mmagrar.info',
                                'portal.mmagrar.info',
                                'relay.mmagrar.info',
                                'smtp.mmagrar.info',
                                'www.mmagrar.de',
                                'www.mmagrar.info',
                                'www.portal.mmagrar.info',
                                'www377.your-server.de',
                            ],
                            port: 222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '78.46.152.62',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mail.mawista.de',
                                'mail.mawista.info',
                                'mail.mawista.org',
                                'dedi2970.your-server.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '78.46.155.156',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'mail.mawista.de',
                                'mail.mawista.info',
                                'mail.mawista.org',
                                'dedi2970.your-server.de',
                            ],
                            port: 222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '78.46.155.156',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'www280.your-server.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '78.47.69.36',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'www280.your-server.de',
                            ],
                            port: 222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '78.47.69.36',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '80.122.63.122',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'imap.allianz-ci.com',
                                'imap.allianz-cm.com',
                                'imap.allianz-mg.com',
                                'imap.allianz-sn.com',
                                'pop.allianz-ci.com',
                                'pop.allianz-cm.com',
                                'pop.allianz-mg.com',
                                'pop.allianz-sn.com',
                                'perdition-green.nfrance.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '80.247.228.30',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'imap.allianz-ci.com',
                                'imap.allianz-cm.com',
                                'imap.allianz-mg.com',
                                'imap.allianz-sn.com',
                                'pop.allianz-ci.com',
                                'pop.allianz-cm.com',
                                'pop.allianz-mg.com',
                                'pop.allianz-sn.com',
                                'perdition-blue.nfrance.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '80.247.229.28',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'grid1c.sigmatic.fi',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '80.69.161.36',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'boutiquevignobleslarose.fr',
                                'vignoblesdelarose.fr',
                                'impec238-03.aqserver.net',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '80.93.93.206',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'old.medexpress.ru',
                                'srv157-h-st.jino.ru',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '81.177.135.110',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'old.medexpress.ru',
                                'srv157-h-st.jino.ru',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '81.177.135.110',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'eulerhermes.netmon2.technacy.it',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '81.29.205.112',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'gin.allianz.com',
                                'lvps83-169-47-190.dedicated.hosteurope.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '83.169.47.190',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'dev.justis.ch',
                                'h2847439.stratoserver.net',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '85.214.219.55',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'sftp.multiasistencia.com',
                                'sftp.multiassistance.com',
                                'sftp.multiassistance.fr',
                                'sftp.multiassistentie.com',
                                '82.98.62.85.in-addr.arpa',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '85.62.98.82',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '87.106.126.122',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'test.meinetiroler.at',
                                'www-old.meinetiroler.at',
                                'www-old.meinetiroler.it',
                                'www.test.meinetiroler.at',
                                'www.test.meinetiroler.it',
                                'static.88-198-201-45.clients.your-server.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '88.198.201.45',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '89.149.166.61',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'admin.mondial-assistance-italia.it',
                                'previndustria.it',
                                'www.previndustria.it',
                                'es1.espero.it',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '89.186.95.213',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'support.vignoblesdelarose.com',
                                'ecs-90-84-191-167.compute.prod-cloud-ocb.orange-business.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '90.84.191.167',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'oficinas.allianz.pt',
                                'redeclinica.allianz.pt',
                                'redeclinicaacpessoais.allianz.pt',
                                'redeclinicaactrabalho.allianz.pt',
                                'vr14.variograma.net',
                            ],
                            port: 2222,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '92.48.69.171',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'ersatzkasse.ch',
                                'www.ersatzkasse.ch',
                            ],
                            port: 2121,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '94.126.20.68',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'bagicin.com',
                                'ftp.bagicin.com',
                                'www.bagicin.com',
                                'static.118.12.130.94.clients.your-server.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '94.130.12.118',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'agrarversicherung.com',
                                'www.agrarversicherung.com',
                                'v22018056266366437.quicksrv.de',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '94.16.117.80',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'medlar.sectio-aurea.org',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '94.16.120.113',
                        },
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                '_collab-edge._tls.nextcarehealth.com',
                                '_sips._tcp.nextcarehealth.com',
                                'nc-expe.nextcarehealth.com',
                            ],
                            port: 22,
                            protocol: 'tcp',
                            service: 'ssh',
                            shortname: 'ssh_pw',
                            target: '94.56.15.26',
                        },
                    ],
                    shortname: 'ssh_pw',
                },
            ],
            mitigation: "> :bulb: **Note**  \n> Please note that throughout this exercise we have used **vim** as editor. Feel free to use any editor you prefer\n\n### Remediation step 1 - Migrate to key-based authentication\n\nTo migrate to key-based authentication, you have to generate a key pair (a private and public key).\n\nComment\n\n- [ ]  **1.1 - Check if you already have a key pair on your system**\n\nTo check, run the following command in the Terminal:\n\n```bash\nls -al ~/.ssh/id_*.pub\n```\n\nIf you already have a key pair, you can use it for authentication. Otherwise, proceed to create new keys as explained in the next step.\n\n- [ ]  **1.2 - Generate a new key pair**\n\nRun the following command in the Terminal:\n\n```bash\nssh-keygen -t rsa\n```\n\nYou will get a notification \"**generating public/private RSA key pair**\". Once done, click **Enter** and accept the default location.\n\n> :bulb: **Note**  \n> You can change the location by typing in a new path. However, it is recommend that you make use of the default location. This is to avoid making unnecessary changes to your SSH client\n\n> You will be given the option to insert a **passphrase**, which serves as an extra layer of security. This is optional, so if you do not want to do this, click **Enter** to continue. After clicking **Enter**, the keys will be created\n> \n\n- [ ]  **1.3 - List your keys**\n\nAfter creating the keys, you can list them by running the following command in the Terminal:\n\n```bash\nls ~/.ssh/id_*\n```\n\n- [ ]  **1.4 - Copy the keys to the remote server**\n\nNext, copy the keys to the server you want to access. To do this, run the following command in the Terminal:\n\n```bash\nssh-copy-id -i ~/.ssh/id_rsa.pub remote_username@server_ip_address\n```\n\nBe sure to change the **server_ip_address** to the server's IP address, and change **remote_username** to the correct username. The remote_username's password will be requested. When you type in the correct password, the public key will be saved on the server.\n\n- [ ]  **1.5 - Log in to your remote server**\n\nNow, you can log in to the server without a password by running the following command in the Terminal:\n\n```bash\nssh remote_username@server_ip_address\n```\n\n### Remediation step 2 - Disable password-based authentication\n\nAfter creating the SSH key pair, you can safely disable password authentication. However, make sure your key-based authentication is set up properly. Verify that you can log in using the authentication key. \n\n- [ ]  **2.1 - Log in to the server using SSH keys**\n\n```bash\nssh remote_username@server_ip_address\n```\n\n- [ ]  **2.2 - Open the SSH configuration either as a root user or as a user with sudo privileges**\n\nRun the following command in the Terminal:\n\n```bash\nsudo vim /etc/ssh/sshd_config\n```\n\n- [ ]  **2.3 - Find the \"PasswordAuthentication\" line and set it to \"no\"**\n\nEdit the file with your preferred text editor and set the `PasswordAuthentication` to `no` as shown below:\n\n```\nPasswordAuthentication no\n```\n\n> :bulb: **Note**  \n> Do not forget to uncomment the line if the **#** is present\n\n- [ ]  **2.4 - Save your changes**\n\n- [ ]  **2.5 - Restart the SSH service to apply the new configuration**\n\n```bash\nsudo systemctl restart sshd\n```\n\n### Remediation step 3 (optional) - Disable the root login\n\nEnabling direct login to root via SSH is a serious security threat as attackers can brute-force your password. To avoid malicious actors snooping around, it is recommended that you disable your SSH root login. \n\n- [ ]  **3.1 - Login as root and open the main configuration file** (**sshd_config file**)\n\nAs root, edit the sshd_config file in `/etc/ssh/sshd_config` by running the following command in the Terminal:\n\n```bash\nsudo vim /etc/ssh/sshd_config\n```\n\n- [ ]  **3.2 - Search for the \"PermitRootLogin yes\" and replace the `yes` with `no`**\n\nEdit the file with your preferred editor as shown below:\n\n```\nPermitRootLogin no\n```\n\n- [ ]  **3.3 - Save the changes and exit your editor**\n\n- [ ]  **3.4 - Restart the SSH service to apply the new configuration**\n\n```bash\nsudo systemctl restart sshd\n```\n\n### Remediation step 4 (optional) - Disable weak Key Exchanges, Ciphers and MACs\n\nTo preserve backwards compatibility, your SSH server might use weak Key Exchange algorithms, ciphers or message authentication codes. You should specifically set the KEXs, ciphers and MACs that your server allows.\n\n- [ ]  **4.1 - Log in to the server using SSH keys**\n\n```bash\nssh remote_username@server_ip_address\n```\n\n- [ ]  **4.2 - Open the SSH configuration either as a root user or as a user with sudo privileges**\n\n```bash\nsudo vim /etc/ssh/sshd_config\n```\n\n- [ ]  **4.3 - Set the values for \"KexAlgorithms\", \"MACs\", \"Ciphers\"**\n\nEdit the file with your preferred text editor and set the **`KexAlgorithms`, `MACs` and `Ciphers`** as shown below:\n\n```\nKexAlgorithms curve25519-sha256@libssh.org,ecdh-sha2-nistp521,ecdh-sha2-nistp384,ecdh-sha2-nistp256,diffie-hellman-group-exchange-sha256\nMACs hmac-sha2-512-etm@openssh.com,hmac-sha2-256-etm@openssh.com,umac-128-etm@openssh.com,hmac-sha2-512,hmac-sha2-256,umac-128@openssh.com\nCiphers chacha20-poly1305@openssh.com,aes256-gcm@openssh.com,aes128-gcm@openssh.com,aes256-ctr,aes192-ctr,aes128-ctr\n```\n\n- [ ]  **4.4 - Save your changes**\n\n- [ ]  **4.5 - Restart the SSH service to apply the new configuration**\n\n```bash\nsudo systemctl restart sshd\n```\n\n### Remediation option 5 (optional) - Set a custom SSH port\n\nPort 22 is the default port for SSH connections. As this is well-known, the majority of hackers target this port. Changing the default SSH port helps mitigate automated attacks and wards off attackers from trying to access your SSH server. \n\n- [ ]  **5.1 - Choose the port you will use**\n\nIt is recommended to use a random port between **49152** and **65535**. This is the range for the so called \"dynamic ports\" (also called private ports).\n\n- [ ]  **5.2 - Login to the server and run the following command in the Terminal**\n\n```bash\nsudo vim /etc/ssh/sshd_config\n```\n\n- [ ]  **5.3 - Find the \"Port 22\" line**\n\n- [ ]  **5.4 - Change the default port to your preferred custom (non-standard) port**\n\n- [ ]  **5.5 - Save the changes**\n\n- [ ]  **5.6 - Restart your SSH service to apply the changes**\n\n```bash\nsudo systemctl restart sshd\n```\n\nNow, SSH is listening on the custom port you specified.\n\n> :bulb: **Note** \n> Your client only knows the default SSH port. Consequently, you have to specify your custom port when connecting\n\n### Remediation step 6 (optional) - Restrict SSH access using iptables\n\nNext, we filter incoming and outgoing traffic to your server. You can set rules that restrict or permit traffic based on IP addresses and other related parameters. This will help to keep away unwanted traffic and minimize brute-force attacks.\n\nFor the sake of demonstration, we will set rules to permit incoming traffic for one IP address. You can replace the example IP address with your own IP address(es):\n\n- [ ]  **6.1 - Whitelist an IP address of your choice**\n\nSSH into your server and execute the following command:\n\n```bash\nsudo iptables -A INPUT -p tcp -s 192.1.1.1 -dport 22 -j ACCEPT\n```\n\n> :bulb: **Note**  \n> Do not forget to change the example IP **192.1.1.1** and port **22** to the IP that you want to whitelist and the port that your SSH server is listening to\n\n- [ ]  **6.2 - Save the previous defined rule**\n\nRun the following command in the Terminal:\n\n```bash\nsudo iptables-save\n```\n\n> :bulb: **Note**  \n> The commands are case-sensitive. In addition, if for any reason you want to reset or get rid of all rules, use the command: **iptables -F**\n\nGreat job, you are all done!",
            number_of_affected_hosts: 156,
            objective: 'Today we are hardening SSH. There are six steps ahead of you to properly secure your SSH:\n\n1. Migrate to key-based authentication\n2. Disable password-based authentication\n3. Disable the root login\n4. Disable weak Key Exchange, Ciphers and MACs\n5. Set a custom SSH Port\n6. Restrict SSH access using iptables',
            risk: 'When using password-based SSH, attackers can brute-force your login credentials and gain access to the node. Besides that, exposing your SSH service broadens your attack surface.\n\nFor details on the exploitation, please refer to [Brute Force Attacks: Definition and Examples](https://www.kaspersky.com/resource-center/definitions/brute-force-attack)',
            title: 'Secure SSH',
            tutorial_name: 'hardening_ssh_configure',
        },
        {
            about: "If a main domain contains subdomain that is pointing to an external service but the external service account was cancelled or has expired. The CNAME which directed to this service still appears in the company's DNS entries. Because the account is not in use anymore, an attacker can claim this account and takeover your subdomain. This is called subdomain takeover and attacker can use this for number of illegitimate purposes. ",
            affected_hosts: [
                {
                    forward_reverse_dns: [
                        '23.227.38.64',
                    ],
                    target: 'shop.clubmarine.co.nz',
                },
            ],
            effort: 1,
            hackability_share: 1.8173557473875512,
            issues: [
                {
                    accepted_on: null,
                    cves: null,
                    fixed_on: null,
                    hosts: [
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                '23.227.38.64',
                            ],
                            port: 0,
                            protocol: '',
                            service: null,
                            shortname: 'subdomain_hijack_high_confidence',
                            target: 'shop.clubmarine.co.nz',
                        },
                    ],
                    shortname: 'subdomain_hijack_high_confidence',
                },
            ],
            mitigation: "There are number of remediation options available.\n\n1. The most simple and best method is to review you DNS entries and remove all the active entries that are no longer in use. Those which are pointing to external services should especially pointed\n2. External services must be configured to listen to the wildcards DNS\n3. When creating a new resource, make the DNS record creation the last step in the process to avoid it from pointing to a non-existing domain\n4. Monitor your DNS records continuously to make sure that there are no non-existing DNS records\n5. Reclaim or purchase the expired domain\n\n### Browser's security for cookies:\n\n- HTTPOnly cookie - Cookies can be accessed by a Javascript code running in the context of the website which created the cookies. HTTPOnly cookie flag indicates that the particular cookie cannot be accessed by Javascript code. The only way to get it is through HTTP request and response headers\n- Secure Cookie - When the cookie has the Secure flag set by the web server, it can be communicated back to the web server only if HTTPS is used",
            number_of_affected_hosts: 1,
            objective: 'Today we are going to mitigate the subdomain take over issue. It is a major security flaw that leads to number of security issues.  ',
            risk: 'If an attacker successfully takes over a subdomain, then number of attacks are possible like: \n\n- Setup a phishing site and send phishing emails to users from legitimate domain to take credentials\n- The subdomain phishing site can be made look legitimate by generating SSL certificates for the hijacked subdomain\n- He may send spam adds to the users\n- He may steal cookies. Cookies contain very sensitive data like session tokens\n- Cross-Site scripting and session hijacking attacks are also possible',
            title: 'Hardening Subdomain Configuration',
            tutorial_name: 'hardening_subdomain_configure',
        },
        {
            about: "Microsoft Exchange Server is Microsoft's email, calendaring, contact, scheduling and collaboration platform. It is deployed on the Windows Server operating system (OS) for business use.",
            affected_hosts: [
                {
                    forward_reverse_dns: [
                        'webmail.allianzsna.com',
                    ],
                    target: '194.126.8.228',
                },
            ],
            effort: 1,
            hackability_share: 3.6347114947751025,
            issues: [
                {
                    accepted_on: null,
                    cves: [
                        'CVE-2021-26855',
                        ' CVE-2021-26857',
                        ' CVE-2021-26858',
                        ' CVE-2021-27065',
                    ],
                    fixed_on: null,
                    hosts: [
                        {
                            false_positive: false,
                            forward_reverse_dns: [
                                'webmail.allianzsna.com',
                            ],
                            port: 443,
                            protocol: 'tcp',
                            service: 'https',
                            shortname: 'msexchange_rce',
                            target: '194.126.8.228',
                        },
                    ],
                    shortname: 'msexchange_rce',
                },
            ],
            mitigation: 'The vulnerability can be mitigated by installing mandatory exchange updates and also by placing the Exchange server in a VPN.\n\n## Remediation option 1 - Install Security Patches\n\n- [ ]  **Security Patching**\n\nIt is recommended for organizations to prioritize installing updates on Exchange Servers that are externally facing.\n\n## Remediation option 2 - Place the Exchange server in a VPN\n\n- [ ]  **Place the server in a VPN**\n\nThe initial attack requires the ability to make an untrusted connection to Exchange server port 443. This can be protected against by restricting untrusted connections, or by setting up a VPN to separate the Exchange server from external access.\n\nMore information on placing exchanger in a VPN: [Protect your Exchange Server with the Barracuda SSL VPN](https://campus.barracuda.com/product/sslvpn/doc/39815463/best-practice-protect-your-exchange-server-with-the-barracuda-ssl-vpn/)',
            number_of_affected_hosts: 1,
            objective: 'The goal today is to prevent Microsoft Exchange Server Remote Code Execution. The issue can be addressed through:\n\n1. Installing updates on Exchange servers\n2. Place the Exchange server in a VPN',
            risk: 'The Microsoft Exchange server attack chain beings with the exploration of this flaw, also known as a server-side-request-forgery (SSRF) vulnerability. The initial attack requires the ability to make an untrusted connection to Exchange server port 443. When exploited, HTTPS connections are established to authenticate user access.\n\nMore information on the vulnerability can be found at: [Microsoft Exchange Server vulnerability](https://msrc.microsoft.com/update-guide/vulnerability/CVE-2021-26855)',
            title: 'Prevent Microsoft Exchange Server Remote Code Execution',
            tutorial_name: 'patching_msexchange',
        },
    ],
};

const cyberFitnessLeaderboardProgress = {
    leaderboard: [
        {
            normalized_score: 6.3,
            quartile: 'top',
            scan_id: '61450bc2-935e-4a0d-b549-26aa47b287da',
        },
        {
            normalized_score: 15.5,
            quartile: 'top',
            scan_id: 'cc064ab6-139e-4e97-bed4-91284ac51692',
        },
        {
            normalized_score: 15.6,
            quartile: 'top',
            scan_id: '1a711445-5e01-4f43-bdf6-128d0264c76f',
        },
        {
            normalized_score: 15.8,
            quartile: 'second',
            scan_id: '651a5641-8353-4a9d-98cb-9fd262a2bda1',
        },
        {
            normalized_score: 17.3,
            quartile: 'second',
            scan_id: '9cbe5e67-f45e-453c-a3c2-89943c7f83a1',
        },
        {
            normalized_score: 17.5,
            quartile: 'second',
            scan_id: '042b1d63-4418-4e03-8457-6ad1e2dbd5a8',
        },
        {
            normalized_score: 17.7,
            quartile: 'third',
            scan_id: '87ff8fe2-8cc6-4695-bdb1-270bddabbcfd',
        },
        {
            normalized_score: 17.7,
            quartile: 'third',
            scan_id: '2aae4a20-bfd1-4f6f-86d6-a62c70b3d954',
        },
        {
            normalized_score: 18.3,
            quartile: 'third',
            scan_id: '295c2905-27fa-40ad-946d-e9a143c4ccbc',
        },
        {
            normalized_score: 18.4,
            quartile: 'bottom',
            scan_id: '29cf0888-1d0d-4f38-911b-409adcdda214',
        },
        {
            normalized_score: 18.5,
            quartile: 'bottom',
            scan_id: '0120a342-ce3d-46ee-be78-e6e775880bdf',
        },
        {
            normalized_score: 21,
            quartile: 'bottom',
            scan_id: '81ac04d6-d3b2-4867-8700-057fb0066efc',
        },
    ],
    progress: [
        {
            finished_at: 1633407335,
            normalized_score: 17.3,
            revision: 20,
        },
        {
            finished_at: 1632934917,
            normalized_score: 15.5,
            revision: 19,
        },
        {
            finished_at: 1632706426,
            normalized_score: 15.6,
            revision: 18,
        },
        {
            finished_at: 1630484488,
            normalized_score: 18.4,
            revision: 17,
        },
        {
            finished_at: 1629760988,
            normalized_score: 17.7,
            revision: 16,
        },
        {
            finished_at: 1627741542,
            normalized_score: 21,
            revision: 15,
        },
        {
            finished_at: 1627906941,
            normalized_score: 17.7,
            revision: 14,
        },
        {
            finished_at: 1627475329,
            normalized_score: 18.3,
            revision: 13,
        },
        {
            finished_at: 1626789909,
            normalized_score: 18.5,
            revision: 12,
        },
        {
            finished_at: 1625713826,
            normalized_score: 19.5,
            revision: 11,
        },
        {
            finished_at: 1623667898,
            normalized_score: 15.3,
            revision: 10,
        },
        {
            finished_at: 1620369458,
            normalized_score: 15.8,
            revision: 9,
        },
        {
            finished_at: 1618144318,
            normalized_score: 17,
            revision: 8,
        },
        {
            finished_at: 1615824125,
            normalized_score: 26.3,
            revision: 7,
        },
        {
            finished_at: 1613566947,
            normalized_score: 5.6,
            revision: 5,
        },
        {
            finished_at: 1610458391,
            normalized_score: 6.3,
            revision: 4,
        },
    ],
};


export { cyberFitnessExercises, cyberFitnessLeaderboardProgress };
